/**
 * ==========================================================================
 *  Liane_Produkte.jsx — Produkt-Verwaltung fuer die Liane-Pflegebox
 * ==========================================================================
 *  Stefan pflegt hier den Produktkatalog (Doktorado + spaetere Lieferanten).
 *
 *  Workflow:
 *    1. „+ Neues Produkt" -> Modal mit Hauptdaten + Bild-Upload
 *    2. In der Produkt-Karte „Varianten verwalten" -> Sub-Modal mit
 *       Liste + Anlegen/Bearbeiten/Loeschen je Variante
 *
 *  Backend: /api/liane/produkte (CRUD), /api/liane/varianten (CRUD),
 *  Bild-Upload als multipart/form-data an /api/liane/produkte/{id}/bild.
 *
 *  Nur Admin darf schreiben — der Backend-Endpoint prueft das, das UI
 *  zeigt die Schreib-Buttons aber allen Cockpit-Usern an (sie laufen ggf.
 *  ins 403). Stefan bekommt von Hendrik einen Admin-Account.
 * ==========================================================================
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Package, Plus, Edit3, Trash2, Save, X, Loader2, AlertTriangle,
  Image as ImageIcon, Upload, Eye, EyeOff, Layers, CheckCircle,
} from 'lucide-react';
import { api, ApiError } from '../api';

// Modal-Wrapper: rendert via Portal direkt an document.body, damit das Modal
// nicht von Stacking-Contexts (Liane-Box-Wrapper, Cockpit-Header) abgeschnitten wird.
function ModalPortal({ children }) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

const CI = { brombeer: '#85366C' };

const TOEPFE = [
  { value: 'monatlich_42_euro',  label: 'Monatlich 42 €-Budget (Standard)' },
  { value: 'jaehrlich_4_stueck', label: 'Jaehrlich max. 4 Stueck (Bettschutz)' },
];

const KATEGORIE_VORSCHLAEGE = [
  'Schutzausruestung',
  'Bettschutz',
  'Haendedesinfektion',
  'Flaechendesinfektion',
];

const BILD_SLOTS = [
  { id: 'haupt',      label: 'Hauptbild (gross)',     hint: 'Detail-Ansicht im Bestellwidget' },
  { id: 'thumbnail',  label: 'Thumbnail (klein)',     hint: 'Listen, Karten, Cockpit-Vorschau' },
  { id: 'anwendung',  label: 'Anwendungsfoto',        hint: 'Lifestyle-Bild „Produkt im Einsatz"' },
];

const LEERES_PRODUKT = {
  id: null,
  titel: '',
  beschreibung: '',
  kurztext: '',
  kategorie: '',
  hersteller: '',
  pflegehilfsmittel_nr: '',
  topf: 'monatlich_42_euro',
  aktiv: true,
  sortierung: 0,
  bilder: {},
};

const LEERE_VARIANTE = {
  id: null,
  material: '',
  groesse: '',
  verpackungseinheit: '',
  ek_preis_netto: '',
  vk_preis_brutto: '',
  aktiv: true,
  sortierung: 0,
};


export default function LianeProdukteVerwaltung() {
  const [produkte, setProdukte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [produktModal, setProduktModal] = useState(null);
  const [variantenModalProdukt, setVariantenModalProdukt] = useState(null);

  const laden = async () => {
    setLoading(true);
    setFehler(null);
    try {
      const liste = await api.get('/api/liane/produkte?inklusive_inaktiv=true');
      setProdukte(Array.isArray(liste) ? liste : []);
    } catch (e) {
      const msg = e instanceof ApiError ? (e.detail || `HTTP ${e.status}`) : 'Verbindung zum Server fehlgeschlagen.';
      setFehler(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { laden(); }, []);

  const oeffneNeu = () => setProduktModal({ ...LEERES_PRODUKT });
  const oeffneEdit = (p) => setProduktModal({
    id: p.id,
    titel: p.titel || '',
    beschreibung: p.beschreibung || '',
    kurztext: p.kurztext || '',
    kategorie: p.kategorie || '',
    hersteller: p.hersteller || '',
    pflegehilfsmittel_nr: p.pflegehilfsmittel_nr || '',
    topf: p.topf || 'monatlich_42_euro',
    aktiv: p.aktiv !== false,
    sortierung: p.sortierung ?? 0,
    bilder: { ...(p.bilder || {}) },
  });

  const deaktivieren = async (p) => {
    if (!confirm(`Produkt „${p.titel}" deaktivieren?\n\nVerschwindet aus dem Bestellwidget, bleibt hier sichtbar und reaktivierbar.`)) return;
    setBusyId(p.id);
    try {
      await api.del(`/api/liane/produkte/${p.id}`);
      await laden();
    } catch (e) {
      alert(e?.detail || 'Deaktivieren fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const reaktivieren = async (p) => {
    setBusyId(p.id);
    try {
      await api.put(`/api/liane/produkte/${p.id}`, { titel: p.titel, aktiv: true });
      await laden();
    } catch (e) {
      alert(e?.detail || 'Reaktivieren fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  const gruppen = useMemo(() => {
    const map = new Map();
    for (const p of produkte) {
      const key = p.kategorie || '— Ohne Kategorie —';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    }
    return Array.from(map.entries());
  }, [produkte]);

  return (
    <div className="max-w-6xl mx-auto p-6 animate-fade-in">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1 flex items-center gap-2">
            <Package className="w-6 h-6 text-gray-500" /> Produktkatalog
          </h2>
          <p className="text-gray-500">
            Stammdaten der bestellbaren Pflegehilfsmittel. Pro Produkt 1..n Varianten
            (z.B. Einmalhandschuhe Nitril/Latex/Vinyl × S/M/L/XL).
          </p>
        </div>
        <button
          onClick={oeffneNeu}
          className="px-4 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors shadow-md hover:-translate-y-0.5 shrink-0 text-white"
          style={{ backgroundColor: CI.brombeer }}
        >
          <Plus className="w-4 h-4" /> Neues Produkt
        </button>
      </div>

      {fehler && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {fehler}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-400 border border-gray-200">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Lade Produkte…
        </div>
      ) : produkte.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-gray-200 shadow-sm">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-700">Noch keine Produkte angelegt</h3>
          <p className="text-gray-500 mb-4">Lege das erste Produkt an, um den Katalog zu starten.</p>
          <button
            onClick={oeffneNeu}
            className="px-4 py-2 rounded-lg font-bold text-sm inline-flex items-center gap-2 text-white"
            style={{ backgroundColor: CI.brombeer }}
          >
            <Plus className="w-4 h-4" /> Erstes Produkt anlegen
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {gruppen.map(([kategorie, eintraege]) => (
            <div key={kategorie}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 ml-1">{kategorie}</h3>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 divide-y divide-gray-100">
                {eintraege.map(p => (
                  <ProduktKarte
                    key={p.id}
                    produkt={p}
                    busy={busyId === p.id}
                    onEdit={() => oeffneEdit(p)}
                    onVarianten={() => setVariantenModalProdukt(p)}
                    onDeaktivieren={() => deaktivieren(p)}
                    onReaktivieren={() => reaktivieren(p)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {produktModal && (
        <ProduktModal
          daten={produktModal}
          setDaten={setProduktModal}
          onClose={() => setProduktModal(null)}
          onGespeichert={async () => { setProduktModal(null); await laden(); }}
        />
      )}

      {variantenModalProdukt && (
        <VariantenModal
          produkt={variantenModalProdukt}
          onClose={() => setVariantenModalProdukt(null)}
          onAenderung={async () => {
            await laden();
            // Modal offen lassen, aber das aktualisierte Produkt nachreichen
            setVariantenModalProdukt(prev =>
              prev ? (produkte.find(p => p.id === prev.id) || prev) : prev
            );
          }}
        />
      )}
    </div>
  );
}


function ProduktKarte({ produkt: p, busy, onEdit, onVarianten, onDeaktivieren, onReaktivieren }) {
  const aktivVarianten = (p.varianten || []).filter(v => v.aktiv !== false);
  const preise = aktivVarianten
    .map(v => v.vk_preis_brutto)
    .filter(x => x !== null && x !== undefined);
  const preisRange = preise.length === 0
    ? '— kein VK-Preis gesetzt —'
    : preise.length === 1
      ? `${preise[0].toFixed(2)} €`
      : `${Math.min(...preise).toFixed(2)} – ${Math.max(...preise).toFixed(2)} €`;

  const bilder = p.bilder || {};
  const karteBildUrl = bilder.thumbnail || bilder.haupt || null;
  return (
    <div className={`p-4 flex items-start gap-4 ${p.aktiv ? '' : 'bg-gray-50/60'}`}>
      <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 overflow-hidden border border-gray-200">
        {karteBildUrl ? (
          <img
            src={karteBildUrl}
            alt={p.titel}
            title={p.kurztext || p.titel}
            className="w-full h-full object-contain"
          />
        ) : (
          <ImageIcon className="w-8 h-8 text-gray-300" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`font-bold ${p.aktiv ? 'text-gray-800' : 'text-gray-500'}`}>{p.titel}</span>
          {!p.aktiv && (
            <span className="bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">inaktiv</span>
          )}
          {p.topf === 'jaehrlich_4_stueck' && (
            <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Jahres-Topf</span>
          )}
          {p.hersteller && <span className="text-xs text-gray-500">· {p.hersteller}</span>}
          {p.pflegehilfsmittel_nr && (
            <span className="text-[10px] text-gray-500 font-mono">PG {p.pflegehilfsmittel_nr}</span>
          )}
        </div>
        <p className="text-sm text-gray-500 line-clamp-2">{p.kurztext || p.beschreibung || ''}</p>
        <div className="text-xs text-gray-400 mt-1.5 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {aktivVarianten.length} aktive Variante{aktivVarianten.length === 1 ? '' : 'n'}</span>
          <span>VK: {preisRange}</span>
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onVarianten}
          className="text-gray-700 hover:bg-gray-100 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
          title="Varianten verwalten"
        >
          <Layers className="w-3.5 h-3.5" /> Varianten
        </button>
        <button
          onClick={onEdit}
          className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition-colors"
          title="Bearbeiten"
        >
          <Edit3 className="w-4 h-4" />
        </button>
        {p.aktiv ? (
          <button
            onClick={onDeaktivieren}
            disabled={busy}
            className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg transition-colors disabled:opacity-50"
            title="Deaktivieren"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />}
          </button>
        ) : (
          <button
            onClick={onReaktivieren}
            disabled={busy}
            className="text-emerald-600 hover:bg-emerald-50 p-2 rounded-lg transition-colors disabled:opacity-50"
            title="Wieder aktivieren"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}


function ProduktModal({ daten, setDaten, onClose, onGespeichert }) {
  const istNeu = !daten.id;
  const setFeld = (feld, wert) => setDaten(prev => ({ ...prev, [feld]: wert }));
  const [busySlot, setBusySlot] = useState(null);
  const [speichernBusy, setSpeichernBusy] = useState(false);

  const speichern = async () => {
    const titel = (daten.titel || '').trim();
    if (!titel) { alert('Titel ist Pflicht.'); return; }
    const payload = {
      titel,
      beschreibung: (daten.beschreibung || '').trim() || null,
      kurztext: (daten.kurztext || '').trim() || null,
      kategorie: (daten.kategorie || '').trim() || null,
      hersteller: (daten.hersteller || '').trim() || null,
      pflegehilfsmittel_nr: (daten.pflegehilfsmittel_nr || '').trim() || null,
      topf: daten.topf || 'monatlich_42_euro',
      aktiv: daten.aktiv !== false,
      sortierung: Number(daten.sortierung) || 0,
    };
    setSpeichernBusy(true);
    try {
      if (istNeu) {
        await api.post('/api/liane/produkte', payload);
      } else {
        await api.put(`/api/liane/produkte/${daten.id}`, payload);
      }
      await onGespeichert();
    } catch (e) {
      alert(e?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setSpeichernBusy(false);
    }
  };

  const bildHochladen = async (slot, datei) => {
    if (!datei) return;
    if (!daten.id) {
      alert('Bitte erst das Produkt anlegen (Speichern), dann kannst Du Bilder hochladen.');
      return;
    }
    const fd = new FormData();
    fd.append('datei', datei);
    setBusySlot(slot);
    try {
      const res = await fetch(`/api/liane/produkte/${daten.id}/bild/${slot}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        try { const obj = JSON.parse(text); throw new Error(obj.detail || text); }
        catch { throw new Error(text || `HTTP ${res.status}`); }
      }
      const json = await res.json();
      setDaten(prev => ({
        ...prev,
        bilder: { ...(prev.bilder || {}), [slot]: `${json.bild_url}?t=${Date.now()}` },
      }));
    } catch (e) {
      alert(e?.message || 'Bild-Upload fehlgeschlagen.');
    } finally {
      setBusySlot(null);
    }
  };

  const bildLoeschen = async (slot) => {
    if (!daten.id || !daten.bilder?.[slot]) return;
    if (!confirm('Dieses Bild wirklich entfernen?')) return;
    setBusySlot(slot);
    try {
      await api.del(`/api/liane/produkte/${daten.id}/bild/${slot}`);
      setDaten(prev => {
        const neuBilder = { ...(prev.bilder || {}) };
        delete neuBilder[slot];
        return { ...prev, bilder: neuBilder };
      });
    } catch (e) {
      alert(e?.detail || 'Bild-Loeschen fehlgeschlagen.');
    } finally {
      setBusySlot(null);
    }
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-5 h-5" style={{ color: CI.brombeer }} />
            {istNeu ? 'Neues Produkt anlegen' : 'Produkt bearbeiten'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Produktbilder</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {BILD_SLOTS.map(slot => (
                <BildSlot
                  key={slot.id}
                  slot={slot}
                  url={daten.bilder?.[slot.id]}
                  busy={busySlot === slot.id}
                  produktAngelegt={!!daten.id}
                  onUpload={(datei) => bildHochladen(slot.id, datei)}
                  onLoeschen={() => bildLoeschen(slot.id)}
                />
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">JPEG, PNG, WEBP, GIF · max. 5 MB pro Bild{!daten.id && ' · erst nach erstem Speichern hochladbar'}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Titel *</label>
              <input
                type="text"
                value={daten.titel}
                onChange={e => setFeld('titel', e.target.value)}
                placeholder="z.B. Einmalhandschuhe"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Kategorie</label>
              <input
                type="text"
                value={daten.kategorie}
                onChange={e => setFeld('kategorie', e.target.value)}
                placeholder="z.B. Schutzausruestung"
                list="liane-kategorien"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
              <datalist id="liane-kategorien">
                {KATEGORIE_VORSCHLAEGE.map(k => <option key={k} value={k} />)}
              </datalist>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Hersteller</label>
              <input
                type="text"
                value={daten.hersteller}
                onChange={e => setFeld('hersteller', e.target.value)}
                placeholder="z.B. Peha-soft (Hartmann)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Pflegehilfsmittel-Nr.</label>
              <input
                type="text"
                value={daten.pflegehilfsmittel_nr}
                onChange={e => setFeld('pflegehilfsmittel_nr', e.target.value)}
                placeholder="z.B. 54.45.01.0001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Topf</label>
              <select
                value={daten.topf}
                onChange={e => setFeld('topf', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              >
                {TOEPFE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
              Mouseover-Tooltip (Kurztext)
            </label>
            <input
              type="text"
              value={daten.kurztext}
              onChange={e => setFeld('kurztext', e.target.value)}
              maxLength={140}
              placeholder="Kurze Info, die beim Mouseover ueber das Produktbild im Widget erscheint"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
            />
            <p className="text-[11px] text-gray-400 mt-1">Max. 140 Zeichen — kurz halten, sonst wird die Tooltip-Box zu gross.</p>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Beschreibung</label>
            <textarea
              value={daten.beschreibung}
              onChange={e => setFeld('beschreibung', e.target.value)}
              rows={5}
              placeholder="Marketingtext aus dem Katalog — z.B. 'Medizinische Einmalhandschuhe in den Materialien Nitril, Latex und Vinyl ...'"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Sortierung</label>
              <input
                type="number"
                value={daten.sortierung}
                onChange={e => setFeld('sortierung', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
              />
              <p className="text-[11px] text-gray-400 mt-1">Niedriger = weiter oben im Widget</p>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <input
                type="checkbox"
                id="produkt-aktiv"
                checked={daten.aktiv !== false}
                onChange={e => setFeld('aktiv', e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <label htmlFor="produkt-aktiv" className="text-sm text-gray-700">
                Aktiv — im Bestellwidget anzeigen
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={speichern}
            disabled={speichernBusy}
            className="px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors shadow-sm text-white disabled:opacity-60"
            style={{ backgroundColor: CI.brombeer }}
          >
            {speichernBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {istNeu ? 'Anlegen' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}


function BildSlot({ slot, url, busy, produktAngelegt, onUpload, onLoeschen }) {
  const inputRef = useRef(null);
  return (
    <div className="border border-gray-200 rounded-lg p-2 flex flex-col gap-2">
      <div className="aspect-square bg-gray-50 rounded-md flex items-center justify-center overflow-hidden border border-gray-100 relative">
        {url ? (
          <img src={url} alt="" className="w-full h-full object-contain" />
        ) : (
          <ImageIcon className="w-8 h-8 text-gray-300" />
        )}
      </div>
      <div className="text-center">
        <div className="text-xs font-bold text-gray-700">{slot.label}</div>
        <div className="text-[10px] text-gray-400 leading-tight">{slot.hint}</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={e => { onUpload(e.target.files?.[0]); e.target.value = ''; }}
        className="hidden"
      />
      <div className="flex gap-1">
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy || !produktAngelegt}
          className="flex-1 px-2 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-50"
          title={!produktAngelegt ? 'Erst Produkt anlegen, dann Bilder hochladen' : ''}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {url ? 'Ersetzen' : 'Hochladen'}
        </button>
        {url && (
          <button
            onClick={onLoeschen}
            disabled={busy}
            className="px-2 py-1.5 text-red-500 hover:bg-red-50 rounded-md disabled:opacity-50"
            title="Bild entfernen"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}


function VariantenModal({ produkt, onClose, onAenderung }) {
  const [bearbeitet, setBearbeitet] = useState(null); // null = Liste, sonst { id?, ...felder }
  const [busyId, setBusyId] = useState(null);
  const varianten = produkt.varianten || [];

  const oeffneNeu = () => setBearbeitet({ ...LEERE_VARIANTE });
  const oeffneEdit = (v) => setBearbeitet({
    id: v.id,
    material: v.material || '',
    groesse: v.groesse || '',
    verpackungseinheit: v.verpackungseinheit || '',
    ek_preis_netto: v.ek_preis_netto ?? '',
    vk_preis_brutto: v.vk_preis_brutto ?? '',
    aktiv: v.aktiv !== false,
    sortierung: v.sortierung ?? 0,
  });

  const speichern = async () => {
    if (!bearbeitet) return;
    const payload = {
      material: (bearbeitet.material || '').trim() || null,
      groesse: (bearbeitet.groesse || '').trim() || null,
      verpackungseinheit: (bearbeitet.verpackungseinheit || '').trim() || null,
      ek_preis_netto: bearbeitet.ek_preis_netto === '' ? null : Number(bearbeitet.ek_preis_netto),
      vk_preis_brutto: bearbeitet.vk_preis_brutto === '' ? null : Number(bearbeitet.vk_preis_brutto),
      aktiv: bearbeitet.aktiv !== false,
      sortierung: Number(bearbeitet.sortierung) || 0,
    };
    try {
      if (bearbeitet.id) {
        await api.put(`/api/liane/varianten/${bearbeitet.id}`, payload);
      } else {
        await api.post(`/api/liane/produkte/${produkt.id}/varianten`, payload);
      }
      setBearbeitet(null);
      await onAenderung();
    } catch (e) {
      alert(e?.detail || 'Speichern fehlgeschlagen.');
    }
  };

  const deaktivieren = async (v) => {
    if (!confirm('Diese Variante deaktivieren? Bleibt sichtbar, aber nicht mehr bestellbar.')) return;
    setBusyId(v.id);
    try {
      await api.del(`/api/liane/varianten/${v.id}`);
      await onAenderung();
    } catch (e) {
      alert(e?.detail || 'Deaktivieren fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <ModalPortal>
    <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Layers className="w-5 h-5" style={{ color: CI.brombeer }} />
              Varianten — {produkt.titel}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Mindestens eine Variante pro Produkt. Bei Produkten ohne Material/Groesse-Optionen genuegt eine Default-Variante.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {!bearbeitet ? (
            <>
              <div className="flex justify-end mb-3">
                <button
                  onClick={oeffneNeu}
                  className="px-3 py-1.5 rounded-lg text-sm font-bold flex items-center gap-2 text-white"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  <Plus className="w-3.5 h-3.5" /> Variante hinzufuegen
                </button>
              </div>
              {varianten.length === 0 ? (
                <div className="text-center py-12 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                  <Layers className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>Noch keine Varianten — bitte mindestens eine anlegen.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">Material</th>
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">Groesse</th>
                      <th className="text-left px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">Verpackung</th>
                      <th className="text-right px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">EK netto</th>
                      <th className="text-right px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">VK brutto</th>
                      <th className="text-center px-3 py-2 text-xs font-bold uppercase tracking-wider text-gray-500">Aktiv</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {varianten.map(v => (
                      <tr key={v.id} className={v.aktiv ? '' : 'bg-gray-50/60 text-gray-400'}>
                        <td className="px-3 py-2">{v.material || '—'}</td>
                        <td className="px-3 py-2">{v.groesse || '—'}</td>
                        <td className="px-3 py-2">{v.verpackungseinheit || '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{v.ek_preis_netto != null ? `${v.ek_preis_netto.toFixed(2)} €` : '—'}</td>
                        <td className="px-3 py-2 text-right font-mono">{v.vk_preis_brutto != null ? `${v.vk_preis_brutto.toFixed(2)} €` : '—'}</td>
                        <td className="px-3 py-2 text-center">
                          {v.aktiv ? <CheckCircle className="w-4 h-4 text-emerald-500 inline" /> : <span className="text-xs">aus</span>}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button onClick={() => oeffneEdit(v)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded transition-colors" title="Bearbeiten">
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {v.aktiv && (
                            <button onClick={() => deaktivieren(v)} disabled={busyId === v.id} className="text-gray-500 hover:bg-gray-100 p-1.5 rounded transition-colors disabled:opacity-50" title="Deaktivieren">
                              {busyId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <VariantenForm
              daten={bearbeitet}
              setDaten={setBearbeitet}
              onAbbrechen={() => setBearbeitet(null)}
              onSpeichern={speichern}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Schliessen
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}


function VariantenForm({ daten, setDaten, onAbbrechen, onSpeichern }) {
  const istNeu = !daten.id;
  const setFeld = (feld, wert) => setDaten(prev => ({ ...prev, [feld]: wert }));

  return (
    <div>
      <h4 className="text-sm font-bold text-gray-800 mb-3">{istNeu ? 'Neue Variante anlegen' : 'Variante bearbeiten'}</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Material</label>
          <input
            type="text"
            value={daten.material}
            onChange={e => setFeld('material', e.target.value)}
            placeholder="z.B. Nitril (leer wenn nicht zutreffend)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Groesse</label>
          <input
            type="text"
            value={daten.groesse}
            onChange={e => setFeld('groesse', e.target.value)}
            placeholder="z.B. M oder mit Fluegel"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Verpackung</label>
          <input
            type="text"
            value={daten.verpackungseinheit}
            onChange={e => setFeld('verpackungseinheit', e.target.value)}
            placeholder="z.B. 100 Stueck oder 500 ml"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">EK-Preis netto (€)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={daten.ek_preis_netto}
            onChange={e => setFeld('ek_preis_netto', e.target.value)}
            placeholder="0.00"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">VK-Preis brutto (€)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={daten.vk_preis_brutto}
            onChange={e => setFeld('vk_preis_brutto', e.target.value)}
            placeholder="0.00"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
          />
          <p className="text-[10px] text-gray-400 mt-1">Was die Pflegekasse erstattet (sieht der Endkunde im Widget)</p>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Sortierung</label>
          <input
            type="number"
            value={daten.sortierung}
            onChange={e => setFeld('sortierung', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <input
          type="checkbox"
          id="variante-aktiv"
          checked={daten.aktiv !== false}
          onChange={e => setFeld('aktiv', e.target.checked)}
          className="w-4 h-4 rounded border-gray-300"
        />
        <label htmlFor="variante-aktiv" className="text-sm text-gray-700">Aktiv — im Bestellwidget anzeigen</label>
      </div>

      <div className="flex items-center justify-end gap-2 mt-5">
        <button
          onClick={onAbbrechen}
          className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-200 transition-colors"
        >
          Abbrechen
        </button>
        <button
          onClick={onSpeichern}
          className="px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2 shadow-sm text-white"
          style={{ backgroundColor: CI.brombeer }}
        >
          <Save className="w-4 h-4" />
          {istNeu ? 'Variante anlegen' : 'Variante speichern'}
        </button>
      </div>
    </div>
  );
}
